import { cn } from '../../../../lib/utils';
import { CheckCircleFill } from '@crabs-ai/web-icons';
import { cva } from 'class-variance-authority';

export interface MenuItemSingleSelectProps extends React.ComponentProps<'div'> {
  size?: 'lg' | 'md';
  showLeadingIcon?: boolean;
  showLabel?: boolean;
  icon?: React.ReactNode;
  text?: React.ReactNode;
  subText?: string;
  showSubText?: boolean;
  label?: React.ReactNode;
  isSelected?: boolean;
}

const MenuItemSingleSelectVariants = cva(
  [
    'menu-item flex items-center gap-2xs rounded-xs justify-between cursor-pointer transition-all',
    'hover:bg-sys-bg-surface-1-hover active:bg-sys-bg-surface-1-press',
    '[&_path]:fill-sys-cont-neutral-default',
  ],
  {
    variants: {
      size: {
        lg: ['py-xs px-sm', '[&_svg]:size-[24px]'],
        md: ['py-2xs px-xs', '[&_svg]:size-[20px]'],
      },
      isSelected: {
        true: [
          '[&_path]:fill-sys-cont-primary-default',
          '[&_.menu-item-single-select-text]:text-sys-cont-primary-default',
        ],
        false: null,
      },
    },
    defaultVariants: {
      size: 'lg',
    },
  },
);

const textVariants: Record<'lg' | 'md', string> = {
  lg: 'text-body-medium',
  md: 'text-body-small',
};

const subTextVariants: Record<'lg' | 'md', string> = {
  lg: 'text-caption-medium',
  md: 'text-caption-small',
};

export default function MenuItemSingleSelect({
  size = 'lg',
  showLeadingIcon = true,
  showLabel = true,
  icon,
  text,
  subText,
  showSubText = true,
  label,
  isSelected = false,
  className,
  ...props
}: MenuItemSingleSelectProps) {
  return (
    <div
      className={cn(
        MenuItemSingleSelectVariants({ size, isSelected }),
        className,
      )}
      {...props}
    >
      <div className="flex items-center gap-2xs">
        {showLeadingIcon && icon}
        {text && (
          <div
            className={cn(
              'menu-item-single-select-text text-sys-cont-neutral-default',
              textVariants[size],
            )}
          >
            {text}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2xs">
        {showSubText && (
          <div
            className={cn(
              'text-sys-cont-neutral-lightest',
              subTextVariants[size],
            )}
          >
            {subText}
          </div>
        )}
        {showLabel && label && label}
        <div className={isSelected ? 'opacity-100' : 'opacity-0'}>
          <CheckCircleFill />
        </div>
      </div>
    </div>
  );
}
